import { useEffect, useMemo, useState } from 'react';
import { LocateFixed, SlidersHorizontal } from 'lucide-react';
import { detectStore } from '@milk/shared';
import { buildList } from '../lib/view';
import { linkProps } from '../lib/router';
import { currentPosition, noteManualChoice } from '../lib/presence';
import { clearChecked, getState, setStoreView, useStore } from '../store/store';
import { AddBar } from '../components/AddBar';
import { ItemRow } from '../components/ItemRow';
import { Empty, Logo, Spinner, useToast } from '../components/ui';

export function ListScreen() {
  const status = useStore((s) => s.status);
  const complete = useStore((s) => s.complete);
  const syncError = useStore((s) => s.syncError);
  const items = useStore((s) => s.items);
  const categories = useStore((s) => s.categories);
  const stores = useStore((s) => s.stores);
  const storeId = useStore((s) => s.storeId);
  const store = storeId ? stores[storeId] ?? null : null;
  const [flash, setFlash] = useState<string | null>(null);
  const toast = useToast();
  const view = useMemo(() => buildList(Object.values(items), Object.values(categories), store), [items, categories, store]);

  useEffect(() => { if (!flash) return; const t = setTimeout(() => setFlash(null), 1300); return () => clearTimeout(t); }, [flash]);

  if (status !== 'ready' || (!complete && !Object.keys(categories).length)) {
    return <div className="center" style={{ padding: 48 }}>{syncError ? <p className="error" role="alert">{syncError}</p> : <Spinner label="Getting the list…" />}</div>;
  }

  return (
    <>
      <h1 className="sr-only">Shopping list{store ? ` for ${store.name}` : ''}</h1>
      <StoreBar storeId={storeId} toBuy={view.toBuy} />
      <AddBar storeId={storeId} onAdded={setFlash} />

      {view.sections.length === 0 && view.cart.length === 0 && (
        <Empty title={view.elsewhere.length ? `Nothing to get at ${store?.name}` : 'The list is empty'}>
          <Logo size={72} />
          <p>{view.elsewhere.length ? 'Everything left is for other stores.' : 'Add something above. Milk, maybe?'}</p>
        </Empty>
      )}

      {view.sections.map(({ category, items: list }) => (
        <section className="section" key={category.id} aria-labelledby={`sec-${category.id}`}>
          <h2 id={`sec-${category.id}`}><span aria-hidden>{category.emoji}</span> {category.name} <span className="n">({list.length})</span></h2>
          <ul className="items">
            {list.map((it) => <ItemRow key={it.id} item={it} storeId={storeId} flash={flash === it.id} />)}
          </ul>
        </section>
      ))}

      {view.elsewhere.length > 0 && (
        <details className="elsewhere">
          <summary>Not at {store?.name} ({view.elsewhere.length})</summary>
          <ul className="items">
            {view.elsewhere.map((it) => <ItemRow key={it.id} item={it} storeId={storeId} />)}
          </ul>
        </details>
      )}

      {view.cart.length > 0 && (
        <section aria-labelledby="cart-head">
          <div className="cart-head">
            <h2 id="cart-head">In the cart ({view.cart.length})</h2>
            <button className="btn" onClick={() => { const n = clearChecked(); toast(`Cleared ${n} item${n === 1 ? '' : 's'}`); }}>Clear checked</button>
          </div>
          <ul className="items">
            {view.cart.map((it) => <ItemRow key={it.id} item={it} storeId={storeId} />)}
          </ul>
        </section>
      )}
    </>
  );
}

/** "All stores" plus each store, as radio chips; and a button to find the store you're in. */
function StoreBar({ storeId, toBuy }: { storeId: string | null; toBuy: number }) {
  const stores = useStore((s) => s.stores);
  const list = Object.values(stores).sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));
  const hasLocations = list.some((s) => s.location);
  const toast = useToast();
  const [locating, setLocating] = useState(false);

  const choose = (id: string | null) => { noteManualChoice(); setStoreView(id); };
  const locate = async () => {
    setLocating(true);
    const pos = await currentPosition(true, true);
    setLocating(false);
    if (!pos) { toast('Couldn’t get your location. Check that location is allowed for this site.', 'error'); return; }
    const hit = detectStore(Object.values(getState().stores), { position: { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracyM: pos.coords.accuracy } });
    if (hit) { setStoreView(hit.store.id); toast(`You’re at ${hit.store.name}`); }
    else toast('You don’t seem to be at any of your stores.');
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (!['ArrowRight', 'ArrowLeft'].includes(e.key)) return;
    const ids = [null, ...list.map((s) => s.id)];
    const i = ids.indexOf(storeId);
    const next = ids[(i + (e.key === 'ArrowRight' ? 1 : ids.length - 1)) % ids.length];
    choose(next);
    requestAnimationFrame(() => document.querySelector<HTMLElement>('.stores-bar [aria-checked="true"]')?.focus());
    e.preventDefault();
  };

  return (
    <div className="stores-bar">
      <div role="radiogroup" aria-label="Shopping at" className="row" style={{ flexWrap: 'nowrap' }} onKeyDown={onKey}>
        <button role="radio" aria-checked={storeId === null} tabIndex={storeId === null ? 0 : -1} className="chip" onClick={() => choose(null)}>
          All stores
        </button>
        {list.map((s) => (
          <button key={s.id} role="radio" aria-checked={storeId === s.id} tabIndex={storeId === s.id ? 0 : -1} className="chip" onClick={() => choose(s.id)}>
            {s.name}{storeId === s.id && <span className="count"> · {toBuy}</span>}
          </button>
        ))}
      </div>
      {hasLocations && (
        <button className="icon-btn" onClick={() => void locate()} aria-label="Find the store I’m in" title="Find the store I’m in" disabled={locating}>
          <LocateFixed size={20} aria-hidden className={locating ? 'spin' : ''} />
        </button>
      )}
      <a className="icon-btn" {...linkProps(storeId ? `/stores/${storeId}` : '/stores')} aria-label="Stores and aisle order" title="Stores and aisle order">
        <SlidersHorizontal size={20} aria-hidden />
      </a>
    </div>
  );
}
